import { RouteManagerContext, useIonRouter } from '@ionic/react';
import { AuthApi } from '@up2stay/up2promo-api-client';
import {
  classifyMagicLinkLoginError,
  classifyMagicLinkRegisterError,
  classifyMagicLinkVerifyError,
  getAuthErrorFieldErrors,
  getAuthErrorStatus,
  isCompleteMagicLinkCode,
  MAGIC_LINK_RESEND_COOLDOWN_MS,
  normalizeMagicLinkCode,
  parseMagicLinkExpiresAt,
  type MagicLinkLoginErrorKey,
  type MagicLinkRegisterErrorKey,
  type MagicLinkVerifyErrorKey,
} from '@up2stay/up2promo-commons/auth';
import { useCallback, useContext } from 'react';
import { shellPaths } from '@/core/router/app-paths';

import { useDictionary } from '@/core/providers/dictionary-provider';
import {
  enterAppShell,
  isRouteManagerReady,
} from '@/core/router/enter-app-shell';
import { useAuthStore } from '@/core/store/auth.store';
import { buildRedirectUrl } from '@/core/utils/redirect-url.utils';

export function useAuthFlow() {
  const dict = useDictionary();
  const router = useIonRouter();
  const routeManager = useContext(RouteManagerContext);
  const authState = useAuthStore();

  const commonDict = dict.auth.common;
  const authLoginDict = dict.auth.login;
  const authRegisterDict = dict.auth.register;
  const errorsDict = dict.auth.errors;
  const userDetailsDict = dict.auth.userDetails;

  const loginErrorMessage = useCallback(
    (key: MagicLinkLoginErrorKey): string => {
      switch (key) {
        case 'emailNotFound':
          // Caller handles requiresRegistration separately for login send.
          return errorsDict.requestFailed;
        case 'userNotActive':
          return authLoginDict.userNotActiveDescription;
        case 'rateLimited':
          return errorsDict.rateLimited;
        case 'requestFailed':
          return errorsDict.requestFailed;
        case 'networkError':
          return errorsDict.networkError;
      }
    },
    [authLoginDict, errorsDict],
  );

  const registerErrorMessage = useCallback(
    (key: MagicLinkRegisterErrorKey): string => {
      switch (key) {
        case 'registrationsDisabled':
          return authRegisterDict.registrationsDisabled;
        case 'emailAlreadyExists':
          return authRegisterDict.userExists;
        case 'usernameAlreadyExists':
          return authRegisterDict.usernameExists;
        case 'rateLimited':
          return errorsDict.rateLimited;
        case 'requestFailed':
          return errorsDict.requestFailed;
        case 'networkError':
          return errorsDict.networkError;
      }
    },
    [authRegisterDict, errorsDict],
  );

  const verifyErrorMessage = useCallback(
    (key: MagicLinkVerifyErrorKey): string => {
      switch (key) {
        case 'incompleteCode':
          return 'Please enter the complete 6-digit code.';
        case 'invalidCode':
          return errorsDict.invalidCode;
        case 'tooManyAttempts':
          return errorsDict.tooManyAttempts;
        case 'expiredCode':
          return errorsDict.expiredCode;
        case 'codeNotFound':
          return errorsDict.codeNotFound;
        case 'emailNotFound':
          return authLoginDict.userNotFound;
        case 'userNotActive':
          return authLoginDict.userNotActiveDescription;
        case 'verificationFailed':
          return errorsDict.verificationFailed;
        case 'unknownError':
          return errorsDict.unknownError;
      }
    },
    [authLoginDict, errorsDict],
  );

  const buildRedirectTemplate = useCallback(() => {
    if (typeof window === 'undefined') return undefined;
    // Match backend default + Maildev “Eingabeseite öffnen” link shape.
    return buildRedirectUrl(
      `/auth/login?account={{email}}&magicCode={{magicCode}}`,
    );
  }, []);

  const sendLoginCode = useCallback(
    async (email: string) => {
      try {
        const redirectTemplate = buildRedirectTemplate();
        const response = await AuthApi.login({
          body: {
            email,
            redirectUrl: redirectTemplate,
          },
          headers: {
            origin: typeof window !== 'undefined' ? window.location.origin : '',
            referer: typeof window !== 'undefined' ? window.location.href : '',
          },
        });

        const expiresAt = parseMagicLinkExpiresAt(response.data);
        const nextResendAt = Date.now() + MAGIC_LINK_RESEND_COOLDOWN_MS;

        return {
          success: true as const,
          expiresAt,
          resendAvailableAt: nextResendAt,
        };
      } catch (err) {
        const key = classifyMagicLinkLoginError(err);
        if (key === 'emailNotFound') {
          return { success: false as const, requiresRegistration: true };
        }
        return { success: false as const, error: loginErrorMessage(key) };
      }
    },
    [buildRedirectTemplate, loginErrorMessage],
  );

  const resolveLogin = useCallback(
    async (email: string) => {
      try {
        const response = await AuthApi.resolveLogin({
          body: { email },
        });
        return {
          success: true as const,
          hasPassword: Boolean(response.data?.hasPassword),
        };
      } catch (err) {
        const key = classifyMagicLinkLoginError(err);
        if (key === 'emailNotFound') {
          return { success: false as const, requiresRegistration: true };
        }
        return { success: false as const, error: loginErrorMessage(key) };
      }
    },
    [loginErrorMessage],
  );

  const sendRegisterCode = useCallback(
    async (
      email: string,
      firstName: string,
      lastName: string,
      username?: string,
    ) => {
      try {
        const redirectTemplate = buildRedirectTemplate();
        const response = await AuthApi.register({
          body: {
            email,
            firstName,
            lastName,
            ...(username && { username }),
            redirectUrl: redirectTemplate,
          },
          headers: {
            origin: typeof window !== 'undefined' ? window.location.origin : '',
            referer: typeof window !== 'undefined' ? window.location.href : '',
          },
        });

        const expiresAt = parseMagicLinkExpiresAt(response.data);
        const nextResendAt = Date.now() + MAGIC_LINK_RESEND_COOLDOWN_MS;

        return {
          success: true as const,
          expiresAt,
          resendAvailableAt: nextResendAt,
        };
      } catch (err) {
        return {
          success: false as const,
          error: registerErrorMessage(classifyMagicLinkRegisterError(err)),
        };
      }
    },
    [buildRedirectTemplate, registerErrorMessage],
  );

  const finishLogin = useCallback(
    (
      user: {
        firstName?: string | null;
        lastName?: string | null;
        username?: string | null;
      },
      tokens: { token: string; refreshToken: string },
    ) => {
      authState.setTokens({
        accessToken: tokens.token,
        refreshToken: tokens.refreshToken,
      });

      const isIncomplete =
        !user.firstName ||
        !user.lastName ||
        !user.username ||
        user.firstName.trim() === '' ||
        user.lastName.trim() === '' ||
        user.username.trim() === '';

      if (isIncomplete) {
        return {
          success: true as const,
          requiresCompletion: true as const,
          user: {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            username: user.username || '',
          },
        };
      }

      authState.setUser(user as never);
      const returnUrl =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('returnUrl') ||
            sessionStorage.getItem('authReturnUrl')
          : null;

      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('authReturnUrl');
      }

      const redirectTo =
        returnUrl && returnUrl.startsWith('/') ? returnUrl : shellPaths.home;
      enterAppShell(
        router,
        isRouteManagerReady(routeManager) ? routeManager : undefined,
        redirectTo,
      );
      return { success: true as const, requiresCompletion: false as const };
    },
    [authState, router, routeManager],
  );

  const verifyCode = useCallback(
    async (email: string, code: string) => {
      const normalizedCode = normalizeMagicLinkCode(code);

      if (!isCompleteMagicLinkCode(normalizedCode)) {
        return {
          success: false as const,
          error: verifyErrorMessage('incompleteCode'),
        };
      }

      try {
        const result = await AuthApi.verifyMagicLink({
          body: {
            email,
            code: normalizedCode,
          },
        });

        return finishLogin(result.data.user, result.data);
      } catch (err) {
        return {
          success: false as const,
          error: verifyErrorMessage(classifyMagicLinkVerifyError(err)),
        };
      }
    },
    [verifyErrorMessage, finishLogin],
  );

  const loginWithPassword = useCallback(
    async (email: string, password: string) => {
      try {
        const result = await AuthApi.loginWithPassword({
          body: { email, password },
        });
        return finishLogin(result.data.user, result.data);
      } catch {
        return {
          success: false as const,
          error: errorsDict.invalidPassword,
        };
      }
    },
    [errorsDict.invalidPassword, finishLogin],
  );

  const updateUserDetails = useCallback(
    async (firstName: string, lastName: string, username?: string) => {
      try {
        const response = await AuthApi.update({
          body: {
            firstName,
            lastName,
            ...(username && { username }),
          },
        });

        authState.setUser(response.data);
        // Check for returnUrl in query params or sessionStorage
        const returnUrl =
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('returnUrl') ||
              sessionStorage.getItem('authReturnUrl')
            : null;

        // Clear stored returnUrl
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('authReturnUrl');
        }

        const redirectTo =
          returnUrl && returnUrl.startsWith('/') ? returnUrl : shellPaths.home;
        enterAppShell(
          router,
          isRouteManagerReady(routeManager) ? routeManager : undefined,
          redirectTo,
        );
        return { success: true as const };
      } catch (err) {
        if (getAuthErrorStatus(err) === 422) {
          const requestErrors = getAuthErrorFieldErrors(err);
          if (
            requestErrors?.username === 'usernameAlreadyExists' ||
            requestErrors?.username === 'alreadyExists'
          ) {
            return {
              success: false as const,
              error: authRegisterDict.usernameExists,
            };
          }
          return { success: false as const, error: errorsDict.requestFailed };
        }
        return { success: false as const, error: errorsDict.networkError };
      }
    },
    [authState, router, routeManager, authRegisterDict, errorsDict],
  );

  return {
    sendLoginCode,
    resolveLogin,
    sendRegisterCode,
    verifyCode,
    loginWithPassword,
    updateUserDetails,
    commonDict,
    authLoginDict,
    authRegisterDict,
    errorsDict,
    userDetailsDict,
  };
}
